import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Button, Popconfirm, Tag, Select, Tooltip, Popover } from "antd";
import {
  DeleteFilled,
  QuestionCircleOutlined,
  EditFilled,
} from "@ant-design/icons";
import usersActions from "../../../redux/users/actions";
import PageHeader from "../../../components/Common/PageHeader/PageHeader";
import AdminUsersEditModal from "../../../components/Admin/AdminUsersComponents/AdminUsersEditModal";
import AdminUsersSearchInput from "../../../components/Admin/AdminUsersComponents/AdminUsersSearchInput";
import "./index.css";

const Users = () => {
  const dispatch = useDispatch();
  const { users, reloadUsers, allAccess, userDetails } = useSelector(
    (state) => state.usersReducer
  );
  const { getUsersRequest, deleteUser, getAllAccess, getUserDetails } =
    usersActions;
  const [user, setUser] = useState({});
  const [openAdminUserEditModal, setOpenAdminUserEditModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [requestedUserId, setRequestedUserId] = useState(null);
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState([]);
  const [selectedAccessIds, setSelectedAccessIds] = useState([]);
  const [sortKey, setSortKey] = useState("registeredDesc");

  useEffect(() => {
    dispatch(getUsersRequest());
    dispatch(getAllAccess());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadUsers]);

  useEffect(() => {
    if (userDetails && requestedUserId !== null && userDetails.id === requestedUserId) {
      setUser(userDetails);
      setOpenAdminUserEditModal(true);
    }
  }, [userDetails, requestedUserId]);

  const onlyCheckedOrganizations = (array) => {
    return array?.filter((x) => x.isChecked === true);
  };

  const resolveOrganizations = (organizations) => {
    if (!organizations || organizations.length === 0) {
      return [];
    }

    const checked = organizations.filter((org) => org.isChecked === true);
    return checked.length > 0 ? checked : organizations;
  };

  const saveSearchValue = (data) => {
    setSearchValue(data);
  };

  const normalizeMultiSelect = (values) => {
    if (!values || values.length === 0) {
      return [];
    }

    if (values.includes("all")) {
      return ["all"];
    }

    return values;
  };

  const organizationOptions = [
    { label: "Всички", value: "all" },
    ...Array.from(
      new Map(
        users
          .flatMap((item) => item.organizations || [])
          .map((organization) => [organization.id, organization])
      ).values()
    )
      .map((organization) => ({
        label: organization.name,
        value: organization.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const accessOptions = [
    { label: "Всички", value: "all" },
    ...allAccess
      .map((access) => ({
        label: access.accessName || access.name,
        value: access.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const accessColors = ["#e0f2fe", "#ede9fe", "#dcfce7", "#fef3c7", "#ffe4e6"];
  const accessTextColors = ["#075985", "#5b21b6", "#166534", "#92400e", "#9f1239"];

  const columns = [
    {
      title: "Име",
      dataIndex: "firstName",
      rowKey: "firstName",
      key: "firstName",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Фамилия",
      dataIndex: "lastName",
      rowKey: "lastName",
      key: "lastName",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      rowKey: "email",
      key: "email",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Организации",
      dataIndex: "organizations",
      rowKey: "organizations",
      render: (text, record) => (
        <>
          {record?.role === 1 ? (
            <Tag
              bordered={false}
              color="red"
              className="admin-users-table-organizations-tags"
            >
              Админ акаунт
            </Tag>
          ) : resolveOrganizations(record?.organizations).length > 2 ? (
            <>
              {resolveOrganizations(record?.organizations)
                .slice(0, 2)
                .map((org) => (
                  <Tag
                    key={org.id}
                    bordered={false}
                    color="blue"
                    className="admin-users-table-organizations-tags"
                  >
                    {org.name}
                  </Tag>
                ))}
              <Popover
                trigger="hover"
                content={
                  <div className="admin-users-org-popover">
                    {resolveOrganizations(record?.organizations).map((org) => (
                      <div key={org.id} className="admin-users-org-popover-item">
                        {org.name}
                      </div>
                    ))}
                  </div>
                }
              >
                <Tag
                  bordered={false}
                  color="blue"
                  className="admin-users-table-organizations-tags admin-users-org-overflow"
                >
                  +{resolveOrganizations(record?.organizations).length - 2}
                </Tag>
              </Popover>
            </>
          ) : (
            resolveOrganizations(record?.organizations).map((org) => (
              <Tag
                key={org.id}
                bordered={false}
                color="blue"
                className="admin-users-table-organizations-tags"
              >
                {org.name}
              </Tag>
            ))
          )}
        </>
      ),
    },
    {
      title: "Права",
      dataIndex: "accessInformation",
      rowKey: "accessInformation",
      render: (accessInformation) => (
        <span className="admin-user-access-badges">
          {accessInformation?.map((access, index) => {
            const colorIndex = index % accessColors.length;
            return (
              <Tooltip key={access.id} title={access.name}>
                <span
                  className="admin-user-access-tag"
                  style={{
                    backgroundColor: accessColors[colorIndex],
                    color: accessTextColors[colorIndex],
                  }}
                >
                  {access.name}
                </span>
              </Tooltip>
            );
          })}
        </span>
      ),
    },
    {
      title: "Последен вход",
      dataIndex: "lastLogin",
      rowKey: "lastLogin",
      render: (text) =>
        text !== "0001-01-01T00:00:00" && (
          <span>{text.replace("T", " ").split(".")[0]}</span>
        ),
    },
    {
      title: "Редактирай",
      dataIndex: "Edit",
      rowKey: "edit",
      render: (text, record) => (
        <Button
          onClick={() => {
            setRequestedUserId(record.id);
            dispatch(getUserDetails(record.id));
          }}
        >
          <EditFilled />
        </Button>
      ),
    },
    {
      title: "Изтрий",
      dataIndex: "delete",
      rowKey: "delete",
      render: (text, record) => (
        <Popconfirm
          icon={
            <QuestionCircleOutlined
              style={{
                color: "red",
              }}
            />
          }
          title="Сигурен ли сте?"
          onConfirm={() => dispatch(deleteUser(record))}
          okText="Да"
          cancelText="Не"
        >
          <Button danger>
            <DeleteFilled />
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const normalizedSearchValue = searchValue.trim().toLowerCase();

  const filteredUsers = users
    .filter((user) => {
      if (!normalizedSearchValue) {
        return true;
      }

      const firstNameMatch = (user.firstName || "")
        .toLowerCase()
        .includes(normalizedSearchValue);
      const lastNameMatch = (user.lastName || "")
        .toLowerCase()
        .includes(normalizedSearchValue);
      const emailMatch = (user.email || "")
        .toLowerCase()
        .includes(normalizedSearchValue);

      const organizationMatch = (user.organizations || []).some((org) =>
        org.name?.toLowerCase().includes(normalizedSearchValue)
      );

      return firstNameMatch || lastNameMatch || emailMatch || organizationMatch;
    })
    .filter((user) => {
      if (selectedOrganizationIds.length === 0 || selectedOrganizationIds.includes("all")) {
        return true;
      }

      return (user.organizations || []).some((org) =>
        selectedOrganizationIds.includes(org.id)
      );
    })
    .filter((user) => {
      if (selectedAccessIds.length === 0 || selectedAccessIds.includes("all")) {
        return true;
      }

      return (user.accessInformation || []).some((access) =>
        selectedAccessIds.includes(access.id)
      );
    });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (sortKey === "registeredAsc") {
      return new Date(a.created || 0) - new Date(b.created || 0);
    }

    if (sortKey === "registeredDesc") {
      return new Date(b.created || 0) - new Date(a.created || 0);
    }

    if (sortKey === "lastLoginAsc") {
      return new Date(a.lastLogin || 0) - new Date(b.lastLogin || 0);
    }

    if (sortKey === "lastLoginDesc") {
      return new Date(b.lastLogin || 0) - new Date(a.lastLogin || 0);
    }

    if (sortKey === "organization") {
      const orgA = (a.organizations?.[0]?.name || "").toLowerCase();
      const orgB = (b.organizations?.[0]?.name || "").toLowerCase();
      return orgA.localeCompare(orgB);
    }

    if (sortKey === "access") {
      const accessA = (a.accessInformation?.[0]?.name || "").toLowerCase();
      const accessB = (b.accessInformation?.[0]?.name || "").toLowerCase();
      return accessA.localeCompare(accessB);
    }

    return 0;
  });

  return (
    <>
      <AdminUsersEditModal
        user={user}
        openAdminUserEditModal={openAdminUserEditModal}
        setOpenAdminUserEditModal={setOpenAdminUserEditModal}
      />
      <PageHeader text="Потребители" />
      <AdminUsersSearchInput sendSearchValue={saveSearchValue} />
      <div className="admin-users-filters">
        <Select
          allowClear
          placeholder="Организация"
          mode="multiple"
          showSearch
          options={organizationOptions}
          value={selectedOrganizationIds}
          onChange={(value) => setSelectedOrganizationIds(normalizeMultiSelect(value))}
          filterOption={(input, option) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
          className="admin-users-filter"
        />
        <Select
          allowClear
          placeholder="Права"
          mode="multiple"
          showSearch
          options={accessOptions}
          value={selectedAccessIds}
          onChange={(value) => setSelectedAccessIds(normalizeMultiSelect(value))}
          filterOption={(input, option) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
          className="admin-users-filter"
        />
        <Select
          placeholder="Сортиране"
          value={sortKey}
          onChange={setSortKey}
          className="admin-users-filter"
          options={[
            { label: "Регистрация (нови)", value: "registeredDesc" },
            { label: "Регистрация (стари)", value: "registeredAsc" },
            { label: "Последен вход (нови)", value: "lastLoginDesc" },
            { label: "Последен вход (стари)", value: "lastLoginAsc" },
            { label: "Организация", value: "organization" },
            { label: "Права", value: "access" },
          ]}
        />
      </div>
      <Table
        rowKey="id"
        dataSource={sortedUsers}
        className="admin-users-table"
        columns={columns}
        loading={users?.length === 0}
        pagination={{
          defaultPageSize: 100,
          showSizeChanger: true,
          pageSizeOptions: ["100", "150", "200", "250", "300"],
        }}
      />
    </>
  );
};

export default Users;
